import { useEffect, useRef, useState } from "react";
import {
  Activity, UserPlus, LogIn, Save, Calculator, Mail, Download,
  Heart, MessageCircle, Send, Bell, BellOff,
} from "lucide-react";
import { api } from "../lib/api";
import { fmtMoney } from "../config";

const ICON_MAP = {
  signup: UserPlus,
  login: LogIn,
  deal_saved: Save,
  trade_estimate_run: Calculator,
  trade_email_sent: Mail,
  trade_pdf_downloaded: Download,
  favorite_added: Heart,
  favorite_removed: Heart,
  chat_lead: MessageCircle,
  lead_submitted: Send,
};

const ACCENT_MAP = {
  signup: "text-emerald-600 bg-emerald-50 border-emerald-200",
  login: "text-slate-600 bg-slate-50 border-slate-200",
  deal_saved: "text-[var(--ssc-blue)] bg-blue-50 border-blue-200",
  trade_estimate_run: "text-violet-600 bg-violet-50 border-violet-200",
  trade_email_sent: "text-cyan-700 bg-cyan-50 border-cyan-200",
  trade_pdf_downloaded: "text-cyan-700 bg-cyan-50 border-cyan-200",
  favorite_added: "text-red-600 bg-red-50 border-red-200",
  favorite_removed: "text-red-400 bg-red-50 border-red-200",
  chat_lead: "text-amber-700 bg-amber-50 border-amber-200",
  lead_submitted: "text-emerald-700 bg-emerald-50 border-emerald-200",
};

const LABEL_MAP = {
  signup: "New account",
  login: "Signed in",
  deal_saved: "Saved a deal",
  trade_estimate_run: "Ran trade estimate",
  trade_email_sent: "Emailed PDF estimate",
  trade_pdf_downloaded: "Downloaded PDF",
  favorite_added: "Favorited a vehicle",
  favorite_removed: "Removed favorite",
  chat_lead: "Chatbot handoff",
  lead_submitted: "Submitted a lead",
};

const HOT_EVENTS = new Set([
  "deal_saved", "trade_email_sent", "chat_lead", "lead_submitted", "signup",
]);

const POLL_MS = 8000;

function timeAgo(iso) {
  const d = new Date(iso).getTime();
  const diff = Math.max(0, Date.now() - d);
  const s = Math.floor(diff / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const days = Math.floor(h / 24);
  return `${days}d ago`;
}

export default function ActivityFeed() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("");
  const [busy, setBusy] = useState(true);
  const [notifPerm, setNotifPerm] = useState(
    typeof Notification !== "undefined" ? Notification.permission : "unsupported"
  );
  const [audioOn, setAudioOn] = useState(false);
  const [pulse, setPulse] = useState(0);
  const audioRef = useRef(null);
  const initialFetch = useRef(true);
  // Mutable refs avoid stale-closure bugs inside the interval callback
  const latestRef = useRef(null);
  const filterRef = useRef("");

  // Initial + delta polling
  useEffect(() => {
    let alive = true;
    let timer;

    // Reset the feed whenever filter changes
    filterRef.current = filter;
    latestRef.current = null;
    initialFetch.current = true;
    setItems([]);
    setBusy(true);

    const fetchOnce = async () => {
      try {
        const params = {};
        if (latestRef.current) params.since = latestRef.current;
        if (filterRef.current) params.event_type = filterRef.current;
        const { data } = await api.get("/dealer/activity", { params });
        if (!alive) return;
        if (data.count > 0) {
          if (!initialFetch.current) handleNewItems(data.items);
          setItems((prev) => mergeUnique([...data.items, ...prev]).slice(0, 200));
          latestRef.current = data.latest || latestRef.current;
          setPulse((p) => p + 1);
        } else if (!latestRef.current) {
          // First fetch returned nothing — anchor `since` so subsequent polls
          // only ask for newer events.
          latestRef.current = new Date().toISOString();
        }
        initialFetch.current = false;
      } catch { /* swallow poll errors so UI stays alive */ } finally {
        if (alive) setBusy(false);
      }
    };

    fetchOnce();
    timer = setInterval(fetchOnce, POLL_MS);

    return () => { alive = false; clearInterval(timer); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  function handleNewItems(newItems) {
    const hot = newItems.filter((i) => HOT_EVENTS.has(i.event_type));
    if (hot.length === 0) return;
    const top = hot[0];
    const title = `${LABEL_MAP[top.event_type] || "Activity"} — ${top.user_name || "Visitor"}`;
    const body = top.detail || "";

    // Browser notification
    if (typeof Notification !== "undefined" && Notification.permission === "granted") {
      try {
        const n = new Notification(title, { body, tag: top.id, icon: "/favicon.ico" });
        setTimeout(() => n.close(), 8000);
      } catch { /* some browsers throw on tab focus */ }
    }
    // Audio cue
    if (audioOn && audioRef.current) {
      try { audioRef.current.currentTime = 0; audioRef.current.play(); } catch { /* ignore */ }
    }
  }

  const requestNotifPerm = async () => {
    if (typeof Notification === "undefined") return;
    const p = await Notification.requestPermission();
    setNotifPerm(p);
    if (p === "granted") {
      new Notification("Activity alerts enabled", { body: "You'll get pings when leads heat up.", icon: "/favicon.ico" });
    }
  };

  return (
    <div data-testid="dealer-activity">
      {/* Controls */}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <select
          className="ssc-dealer-input"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          data-testid="activity-filter"
        >
          <option value="">All activity</option>
          <option value="signup">Signups</option>
          <option value="login">Sign-ins</option>
          <option value="deal_saved">Deals saved</option>
          <option value="trade_estimate_run">Trade estimates</option>
          <option value="trade_email_sent">PDFs emailed</option>
          <option value="trade_pdf_downloaded">PDFs downloaded</option>
          <option value="favorite_added">Favorites</option>
          <option value="chat_lead">Chatbot handoffs</option>
          <option value="lead_submitted">Lead forms</option>
        </select>

        {notifPerm !== "granted" && notifPerm !== "unsupported" && (
          <button
            onClick={requestNotifPerm}
            className="ssc-btn ssc-btn-secondary text-xs inline-flex items-center gap-2"
            data-testid="activity-enable-notif"
          >
            <Bell size={13} /> Enable browser alerts
          </button>
        )}
        {notifPerm === "granted" && (
          <span className="text-xs text-emerald-300 inline-flex items-center gap-1"><Bell size={13} /> Alerts on</span>
        )}
        <label className="text-xs text-white/70 inline-flex items-center gap-2 ml-1 cursor-pointer" data-testid="activity-audio-toggle">
          <input type="checkbox" checked={audioOn} onChange={(e) => setAudioOn(e.target.checked)} className="w-3.5 h-3.5" />
          Sound ping
        </label>

        <span className="ml-auto text-xs text-white/50 inline-flex items-center gap-2">
          <span className={`w-2 h-2 rounded-full bg-emerald-400 ${pulse > 0 ? "animate-pulse" : ""}`} />
          Live · polling every {POLL_MS / 1000}s
        </span>
      </div>

      {/* Feed */}
      <div className="bg-white rounded-2xl overflow-hidden">
        {busy && items.length === 0 ? (
          <div className="text-center py-12 text-[var(--ssc-ink-2)] text-sm">Loading activity…</div>
        ) : items.length === 0 ? (
          <div className="text-center py-12 text-[var(--ssc-ink-2)] text-sm" data-testid="activity-empty">
            No activity yet. Customer actions will stream here in real time.
          </div>
        ) : (
          <ul className="divide-y divide-[var(--ssc-line)]" data-testid="activity-list">
            {items.map((ev) => <ActivityRow key={ev.id} ev={ev} />)}
          </ul>
        )}
      </div>

      {/* Tiny notification audio (base64 chime) */}
      <audio ref={audioRef} preload="auto">
        <source
          src="data:audio/mp3;base64,SUQzAwAAAAAAJlRYWFgAAAARAAADbWFqb3JfYnJhbmQAaXNvbQAA//uQRAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA"
          type="audio/mp3"
        />
      </audio>
    </div>
  );
}

function mergeUnique(arr) {
  const seen = new Set();
  return arr.filter((x) => {
    if (seen.has(x.id)) return false;
    seen.add(x.id); return true;
  });
}

function ActivityRow({ ev }) {
  const Icon = ICON_MAP[ev.event_type] || Activity;
  const accent = ACCENT_MAP[ev.event_type] || "text-slate-600 bg-slate-50 border-slate-200";
  const label = LABEL_MAP[ev.event_type] || ev.event_type;
  const isHot = HOT_EVENTS.has(ev.event_type);

  return (
    <li className="px-4 py-3 flex items-center gap-3 hover:bg-[var(--ssc-paper)] transition-colors" data-testid={`activity-row-${ev.event_type}`}>
      <div className={`shrink-0 w-9 h-9 rounded-full border flex items-center justify-center ${accent}`}>
        <Icon size={16} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-bold text-[var(--ssc-ink)] text-sm">{ev.user_name || ev.user_email || "Anonymous visitor"}</span>
          <span className={`text-[10px] font-extrabold uppercase tracking-widest px-2 py-0.5 rounded-full border ${accent}`}>{label}</span>
          {isHot && <span className="text-[10px] font-bold uppercase tracking-widest text-red-600">HOT</span>}
        </div>
        <div className="text-sm text-[var(--ssc-ink-2)] truncate">
          {ev.detail}
          {ev.deal_code && <span className="font-mono font-bold ml-2 text-[var(--ssc-navy)]">{ev.deal_code}</span>}
          {ev.amount ? <span className="font-bold text-[var(--ssc-blue)] ml-2">{fmtMoney(ev.amount)}</span> : null}
        </div>
        {(ev.user_email || ev.user_phone) && (
          <div className="text-xs text-[var(--ssc-ink-2)] mt-0.5">
            {ev.user_email && <a className="text-[var(--ssc-blue)]" href={`mailto:${ev.user_email}`}>{ev.user_email}</a>}
            {ev.user_email && ev.user_phone && <span> · </span>}
            {ev.user_phone && <a className="text-[var(--ssc-ink)]" href={`tel:${ev.user_phone}`}>{ev.user_phone}</a>}
          </div>
        )}
      </div>
      <div className="text-xs text-[var(--ssc-ink-2)] shrink-0">{timeAgo(ev.created_at)}</div>
    </li>
  );
}
